import { Box, Typography, Divider, Link } from '@mui/material';
import LegalPageLayout from '../../components/legal/LegalPageLayout';

const NAVY = '#0a326d';
const TEXT = '#334155';

function Section({ number, title, children }) {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} color={NAVY} sx={{ mb: 1 }}>
        {number}. {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>{children}</Box>
    </Box>
  );
}

function P({ children }) {
  return (
    <Typography variant="body2" sx={{ color: TEXT, lineHeight: 1.7, textAlign: 'justify' }}>
      {children}
    </Typography>
  );
}

function EmailLine({ address }) {
  return (
    <Typography variant="body2" sx={{ fontWeight: 700 }}>
      <Link href={`mailto:${address}`}>{address}</Link>
    </Typography>
  );
}

export default function ContactSupport() {
  return (
    <LegalPageLayout>
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant="h5"
          fontWeight={800}
          color="#072146"
          sx={{ letterSpacing: 0.3, fontSize: { xs: '1.15rem', sm: '1.4rem', md: '1.5rem' } }}
        >
          TIFOXR CONTACT US
        </Typography>
        <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
          Last Updated: August 21, 2026
        </Typography>
      </Box>

      <Divider sx={{ my: 1 }} />

      <P>
        This Contact Us page applies to the TifoXR public website, TifoXR Immersive, and TifoXR CMS. Use the
        contact information below to direct your question or request to the appropriate team.
      </P>

      <Section number={1} title="Technical and Product Support">
        <P>
          For technical, account, login, access, platform, or product-related questions concerning the TifoXR
          public website, TifoXR Immersive, or TifoXR CMS, please contact:
        </P>
        <EmailLine address="support@globalmobisoft.com" />
        <P>
          When contacting support, please identify the relevant TifoXR environment and provide sufficient
          information to help us understand and address the issue.
        </P>
      </Section>

      <Section number={2} title="Privacy and Data Protection">
        <P>
          For questions or requests concerning personal information, privacy, data protection, or applicable
          data-subject rights relating to TifoXR:
        </P>
        <EmailLine address="compliance@globalmobisoft.com" />
        <P>
          Where a request relates to a club-specific deployment, GMS may coordinate with the applicable club or
          other relevant data controller where appropriate.
        </P>
      </Section>

      <Section number={3} title="Content and Abuse Reports">
        <P>
          For reports concerning potentially illegal content, abusive content, violations of TifoXR terms, or
          other content-related concerns:
        </P>
        <EmailLine address="compliance@globalmobisoft.com" />
        <P>For Digital Services Act-related reports, please use the subject line: "DSA Report".</P>
        <P>Where available, users may also report content through the in-app Report functionality.</P>
        <P>
          Reports should include sufficient information to allow GMS to identify and assess the relevant content,
          such as the relevant URL, screenshot, timestamp, username, or other identifying information.
        </P>
        <P>
          Additional information about TifoXR content moderation, reporting, statements of reasons, appeals, and
          applicable Digital Services Act procedures is available on the TifoXR DSA Information, Content
          Moderation, and Abuse Reporting page.
        </P>
      </Section>

      <Section number={4} title="DSA Appeals">
        <P>
          If you wish to appeal a TifoXR content or account restriction covered by the Digital Services Act:
        </P>
        <EmailLine address="compliance@globalmobisoft.com" />
        <P>Please use the subject line: "DSA Appeal".</P>
        <P>
          Where applicable, appeals may also be submitted by replying to the statement of reasons provided for the
          relevant restriction.
        </P>
        <P>
          The applicable DSA appeal procedures and timelines are described on the TifoXR DSA Information, Content
          Moderation, and Abuse Reporting page.
        </P>
      </Section>

      <Section number={5} title="Security Concerns">
        <P>
          If you believe you have identified a security vulnerability, unauthorized access, or another security
          issue affecting the TifoXR public website, TifoXR Immersive, or TifoXR CMS, please contact:
        </P>
        <EmailLine address="compliance@globalmobisoft.com" />
        <P>
          Please provide the affected TifoXR environment, relevant URL or functionality, and a description of the
          issue, together with any other information that may assist with investigation.
        </P>
      </Section>

      <Section number={6} title="EU and UK Representative">
        <P>For matters relating to TifoXR and GMS's EU or UK representation:</P>
        <Box component="address" sx={{ fontStyle: 'normal', color: TEXT, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.7 }}>
            Data Protection Representative Limited (DataRep)
          </Typography>

          <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.7, mt: 1 }}>
            EU Representative:
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>77 Camden Street Lower</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>Dublin D02 XE80</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>Ireland</Typography>

          <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.7, mt: 1 }}>
            UK Representative:
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>107-111 Fleet Street</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>London EC4A 2AB</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>United Kingdom</Typography>

          <Typography variant="body2" sx={{ lineHeight: 1.7, mt: 1 }}>
            Email:{' '}
            <Link href="mailto:tifoxr@datarep.com" sx={{ fontWeight: 700 }}>
              tifoxr@datarep.com
            </Link>
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Portal:{' '}
            <Link href="https://www.datarep.com/tifoxr" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700 }}>
              https://www.datarep.com/tifoxr
            </Link>
          </Typography>
        </Box>
      </Section>

      <Section number={7} title="Club-Specific Matters">
        <P>
          For matters relating specifically to a club's TifoXR deployment, including club-controlled personal
          information, club-specific content, purchases, tickets, subscriptions, or other club services, you may
          also need to contact the applicable club directly.
        </P>
        <P>
          Where appropriate, GMS may direct the request to the relevant club or coordinate with the club to
          address the matter.
        </P>
      </Section>

      <Section number={8} title="TifoXR Contact Information">
        <P>Technical and product support:</P>
        <EmailLine address="support@globalmobisoft.com" />
        <P>Privacy, compliance, DSA, content reports, appeals, and security:</P>
        <EmailLine address="compliance@globalmobisoft.com" />
        <P>EU/UK representative contact:</P>
        <EmailLine address="tifoxr@datarep.com" />
      </Section>
    </LegalPageLayout>
  );
}
